import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface SharedBoard {
  id: string;
  board_id: string;
  shared_by: string;
  shared_with: string | null;
  share_token: string | null;
  permission: 'view' | 'edit' | 'admin';
  is_active: boolean;
  created_at: string;
  expires_at: string | null;
  board: {
    id: string;
    name: string;
    icon_color: string;
    created_at: string;
  };
  shared_by_user?: {
    email: string;
  };
}

export function useSharedBoards() {
  const [sharedBoards, setSharedBoards] = useState<SharedBoard[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchSharedBoards = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setSharedBoards([]);
        setIsLoading(false);
        return;
      }

      // Get boards shared with current user
      const { data, error } = await supabase
        .from('board_shares')
        .select(`
          *,
          board:boards(id, name, icon_color, created_at)
        `)
        .eq('shared_with', user.id)
        .eq('is_active', true);

      if (error) {
        console.error('Error fetching shared boards:', error);
        setSharedBoards([]);
        return;
      }

      // Fetch sharer emails separately (user_settings lookup)
      const sharerIds = [...new Set((data || []).map(s => s.shared_by))];
      
      let sharerEmails: Record<string, string> = {};
      if (sharerIds.length > 0) {
        const { data: settings } = await supabase
          .from('user_settings')
          .select('user_id, email')
          .in('user_id', sharerIds);
        
        if (settings) {
          sharerEmails = settings.reduce((acc, s) => {
            acc[s.user_id] = s.email;
            return acc;
          }, {} as Record<string, string>);
        }
      }

      const boardsWithEmails = (data || [])
        .filter(share => share.board) // Only include shares with valid boards
        .map(share => ({
          ...share,
          permission: share.permission as 'view' | 'edit' | 'admin',
          shared_by_user: {
            email: sharerEmails[share.shared_by] || 'Unknown'
          }
        }));

      setSharedBoards(boardsWithEmails as SharedBoard[]);
    } catch (err) {
      console.error('Error in fetchSharedBoards:', err);
      setSharedBoards([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSharedBoards();
  }, [fetchSharedBoards]);

  return { sharedBoards, isLoading, refetch: fetchSharedBoards };
}
